// src/features/merchantConfig/components/editors/ArrayOfScalarsEditor.jsx
//
// Editable tag-style input for arrays of scalars (strings, numbers,
// booleans). Press Enter or comma to commit a new chip; Backspace on empty
// input deletes the last chip. Each commit pushes the new array up via
// `onCommit` — the server replaces arrays wholesale, so callers don't need
// to send a diff.
//
// We coerce each chip's text to the type of the existing items where
// possible:
//   * all-number array  -> incoming chips parsed as Number
//   * all-boolean array -> incoming chips accepted only if "true"/"false"
//   * otherwise         -> kept as strings

import React, { useState } from "react";
import { X } from "lucide-react";
import { inputClass } from "./editorChrome";

/**
 * @param {{
 *   value: Array<string | number | boolean | null>,
 *   onCommit: (next: Array<string | number | boolean>) => void,
 *   ariaLabel: string,
 *   hasError?: boolean,
 *   inputId?: string,
 * }} props
 */
export default function ArrayOfScalarsEditor({
  value,
  onCommit,
  ariaLabel,
  hasError,
  inputId,
}) {
  const items = Array.isArray(value) ? value : [];
  const [pending, setPending] = useState("");

  // Pick the chip coercer from the existing items.
  const dominantType = (() => {
    if (items.length === 0) return "string";
    const t = typeof items[0];
    if (items.every((it) => typeof it === t)) return t;
    return "string";
  })();

  const coerceChip = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (dominantType === "number") {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    if (dominantType === "boolean") {
      if (trimmed === "true") return true;
      if (trimmed === "false") return false;
      return null;
    }
    return trimmed;
  };

  const addChip = () => {
    const next = coerceChip(pending);
    if (next === null) {
      setPending("");
      return;
    }
    onCommit([...items, next]);
    setPending("");
  };

  const removeAt = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onCommit(next);
  };

  return (
    <div
      className={[
        "flex flex-wrap items-center gap-1.5 rounded-md border px-2 py-1.5",
        hasError
          ? "border-red-300 focus-within:ring-2 focus-within:ring-red-100"
          : "border-gray-300 focus-within:ring-2 focus-within:ring-blue-100",
        "bg-white",
      ].join(" ")}
    >
      {items.map((it, idx) => (
        <span
          key={`${idx}-${String(it)}`}
          className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800"
        >
          <span>{String(it)}</span>
          <button
            type="button"
            onClick={() => removeAt(idx)}
            aria-label={`Remove ${String(it)}`}
            className="text-blue-500 hover:text-blue-800"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      <input
        id={inputId}
        type="text"
        value={pending}
        aria-label={ariaLabel}
        placeholder={
          items.length === 0
            ? `Type and press Enter to add${dominantType !== "string" ? ` a ${dominantType}` : ""}`
            : "Add another"
        }
        onChange={(e) => setPending(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addChip();
          } else if (e.key === "Backspace" && pending === "" && items.length > 0) {
            e.preventDefault();
            removeAt(items.length - 1);
          }
        }}
        onBlur={() => {
          if (pending.trim()) addChip();
        }}
        className={inputClass({
          hasError,
          extra: "min-w-[8rem] flex-1 border-0 px-1 py-0 shadow-none focus:ring-0",
        })}
      />
    </div>
  );
}
